import React from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'gatsby';
import { ImGithub, ImYoutube, ImLinkedin2, ImTwitter } from 'react-icons/im';
import { device } from '../utils/breakpoints';

const FooterWrapper = styled.footer`
  background: #0F172A;
  color: #ffffff;
  padding: 80px 0 0;
  
  @media ${device.tablet} {
    padding: 60px 0 0;
  }
`;

const FooterTop = styled.div`
  padding-bottom: 60px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterBrand = styled.div`
  margin-bottom: 24px;
`;

const BrandTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 12px;
`;

const BrandDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  max-width: 300px;
`;

const FooterColumn = styled.div`
  margin-bottom: 32px;
`;

const ColumnTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FooterLink = styled(Link)`
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 12px;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #3B82F6;
    text-decoration: none;
  }
`;

const ExternalLink = styled.a`
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 12px;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #3B82F6;
    text-decoration: none;
  }
`;

const NewsletterSection = styled.div`
  margin-bottom: 32px;
`;

const NewsletterTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 12px;
`;

const NewsletterDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 16px;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 8px;
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
  }
`;

const SubscribeButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #FFD700 0%, #FF6B35 100%);
  color: #0F2744;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.4);
    background: linear-gradient(135deg, #FFE84D 0%, #FF8652 100%);
  }
`;

const FooterBottom = styled.div`
  padding: 30px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media ${device.tablet} {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const Copyright = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
`;

const SocialLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  font-size: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    color: #3B82F6;
    transform: translateY(-2px);
  }
`;

const ModernFooter = () => {
  return (
    <FooterWrapper>
      <Container>
        <FooterTop>
          <Row>
            <Col lg={3} md={6} xs={12}>
            <FooterBrand>
              <BrandTitle>Ametnes Platform</BrandTitle>
              <BrandDescription>
                Empowering businesses with AI-enabled opensource enterprise software
                for modern cloud infrastructure.
              </BrandDescription>
            </FooterBrand>
              <NewsletterSection>
                <NewsletterTitle>Stay Updated</NewsletterTitle>
                <NewsletterDescription>
                  Subscribe to our newsletter for updates
                </NewsletterDescription>
                <NewsletterForm onSubmit={(e) => e.preventDefault()}>
                  <EmailInput type="email" placeholder="Your email address" />
                  <SubscribeButton type="submit">Subscribe</SubscribeButton>
                </NewsletterForm>
              </NewsletterSection>
            </Col>
            
            <Col lg={9} md={12}>
              <Row>
                <Col lg={3} md={6} xs={6}>
                  <FooterColumn>
                    <ColumnTitle>Product</ColumnTitle>
                    <FooterLink to="/platform">Platform</FooterLink>
                    <FooterLink to="/platform/public-cloud">Ametnes-Hosted</FooterLink>
                    <FooterLink to="/solutions">Solutions</FooterLink>
                    <FooterLink to="/pricing">Pricing</FooterLink>
                  </FooterColumn>
                </Col>
                
                <Col lg={3} md={6} xs={6}>
                  <FooterColumn>
                    <ColumnTitle>Solutions</ColumnTitle>
                    <FooterLink to="/solutions/ai">AI Solutions</FooterLink>
                    <FooterLink to="/solutions/data">Data & Analytics</FooterLink>
                    <FooterLink to="/solutions/automation">Automation</FooterLink>
                    <FooterLink to="/solutions/devops">Developer</FooterLink>
                    <FooterLink to="/solutions/enterprise">Enterprise</FooterLink>
                    <FooterLink to="/solutions/sme">SME</FooterLink>
                  </FooterColumn>
                </Col>
                
                <Col lg={3} md={6} xs={6}>
                  <FooterColumn>
                    <ColumnTitle>Resources</ColumnTitle>
                    <ExternalLink href="/docs/" target="_blank">Documentation</ExternalLink>
                    <FooterLink to="/case-studies">Case Studies</FooterLink>
                    <FooterLink to="/events">Events</FooterLink>
                    <FooterLink to="/contact">Support</FooterLink>
                  </FooterColumn>
                </Col>
                
                <Col lg={3} md={6} xs={6}>
                  <FooterColumn>
                    <ColumnTitle>Company</ColumnTitle>
                    <FooterLink to="/about">About Us</FooterLink>
                    <FooterLink to="/careers">Careers</FooterLink>
                    <FooterLink to="/contact">Contact</FooterLink>
                  </FooterColumn>
                </Col>
                
                <Col lg={3} md={6} xs={6}>
                  <FooterColumn>
                    <ColumnTitle>Legal</ColumnTitle>
                    <FooterLink to="/privacy">Privacy Policy</FooterLink>
                    <FooterLink to="/terms">Terms of Service</FooterLink>
                  </FooterColumn>
                </Col>
                
                <Col lg={3} md={6} xs={6}>
                  <FooterColumn>
                    <ColumnTitle>Connect</ColumnTitle>
                    <ExternalLink href="https://www.linkedin.com/company/ametnes/" target="_blank">
                      LinkedIn
                    </ExternalLink>
                    <ExternalLink href="https://twitter.com/ametnes" target="_blank">
                      Twitter
                    </ExternalLink>
                    <ExternalLink href="https://github.com/ametnes/" target="_blank">
                      GitHub
                    </ExternalLink>
                    <ExternalLink href="https://www.youtube.com/@ametnescloud" target="_blank">
                      YouTube
                    </ExternalLink>
                  </FooterColumn>
                </Col>
              </Row>
            </Col>
          </Row>
        </FooterTop>
        
        <FooterBottom>
          <Copyright>
            © 2026 Ametnes, Inc. All rights reserved.
          </Copyright>
          <SocialLinks>
            <SocialLink href="https://www.linkedin.com/company/ametnes/" target="_blank" aria-label="LinkedIn">
              <ImLinkedin2 />
            </SocialLink>
            <SocialLink href="https://twitter.com/ametnes" target="_blank" aria-label="Twitter">
              <ImTwitter />
            </SocialLink>
            <SocialLink href="https://github.com/ametnes/" target="_blank" aria-label="GitHub">
              <ImGithub />
            </SocialLink>
            <SocialLink href="https://www.youtube.com/@ametnescloud" target="_blank" aria-label="YouTube">
              <ImYoutube />
            </SocialLink>
          </SocialLinks>
        </FooterBottom>
      </Container>
    </FooterWrapper>
  );
};

export default ModernFooter;


